import React, { useEffect, useRef, useMemo } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import * as THREE from 'three';
import SpriteText from 'three-spritetext';
import { getNodeColor, getEdgeColor, getEdgeStyle } from '../utils/colorScheme';
import { createHypercube, createImpossibleTriangle, animateGeometry } from '../utils/geometryUtils';

const Graph3D = ({ data, filters, onNodeClick, onNodeDoubleClick, selectedNode }) => {
  const fgRef = useRef();

  const filteredData = useMemo(() => {
    if (!data) return { nodes: [], links: [] };

    let nodes = [...data.nodes];
    let edges = [...data.edges];

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      nodes = nodes.filter(node => 
        node.label.toLowerCase().includes(term) ||
        node.keywords?.some(k => k.toLowerCase().includes(term))
      );
      const nodeIds = new Set(nodes.map(n => n.id));
      edges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    }

    if (filters.selectedGroup !== 'all') {
      nodes = nodes.filter(node => node.group === filters.selectedGroup);
      const nodeIds = new Set(nodes.map(n => n.id));
      edges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    }

    if (filters.showEdgeType !== 'all') {
      edges = edges.filter(e => e.type === filters.showEdgeType);
    }

    return { nodes, links: edges };
  }, [data, filters]);

  useEffect(() => {
    if (fgRef.current) {
      const scene = fgRef.current.scene();

      const hypercube1 = createHypercube();
      hypercube1.position.set(-600, 600, -900);
      scene.add(hypercube1);
      animateGeometry(hypercube1, 'y', 0.003);

      const hypercube2 = createHypercube();
      hypercube2.position.set(600, 600, -900);
      scene.add(hypercube2);
      animateGeometry(hypercube2, 'y', -0.003);

      const hypercube3 = createHypercube();
      hypercube3.position.set(-600, -600, -900);
      scene.add(hypercube3);
      animateGeometry(hypercube3, 'x', 0.003);

      const hypercube4 = createHypercube();
      hypercube4.position.set(600, -600, -900);
      scene.add(hypercube4);
      animateGeometry(hypercube4, 'x', -0.003);

      const triangle1 = createImpossibleTriangle();
      triangle1.position.set(0, 500, -700);
      scene.add(triangle1);
      animateGeometry(triangle1, 'z', 0.002);

      const triangle2 = createImpossibleTriangle();
      triangle2.position.set(0, -500, -700);
      scene.add(triangle2);
      animateGeometry(triangle2, 'z', -0.002);

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      const cyanLight = new THREE.DirectionalLight(0x00ffff, 0.7);
      cyanLight.position.set(-1, 1, 1);
      scene.add(cyanLight);

      const violetLight = new THREE.DirectionalLight(0x8b00ff, 0.7);
      violetLight.position.set(1, 1, -1);
      scene.add(violetLight);

      // SPREAD NODES AT INITIAL POSITIONS
      if (filteredData.nodes.length > 0) {
        const radius = 400; // HUGE radius
        filteredData.nodes.forEach((node, i) => {
          const angle = (i / filteredData.nodes.length) * Math.PI * 2;
          node.fx = Math.cos(angle) * radius;
          node.fy = Math.sin(angle) * radius;
          node.fz = (Math.random() - 0.5) * 200;
        });

        // Release fixed positions after 2 seconds
        setTimeout(() => {
          filteredData.nodes.forEach(node => {
            node.fx = undefined;
            node.fy = undefined;
            node.fz = undefined;
          });
        }, 2000);
      }
    }
  }, [filteredData]);

  const nodeThreeObject = (node) => {
    const group = new THREE.Group();
    
    const nodeColor = getNodeColor(node);
    const size = 10;
    
    const geometry = new THREE.SphereGeometry(size, 32, 32);
    const material = new THREE.MeshPhongMaterial({
      color: nodeColor,
      emissive: nodeColor,
      emissiveIntensity: 0.4,
      shininess: 100
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    group.add(mesh);
    
    const glowGeometry = new THREE.SphereGeometry(size * 1.3, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: nodeColor,
      transparent: true,
      opacity: 0.2
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    group.add(glow);
    
    const sprite = new SpriteText(node.label.substring(0, 20));
    sprite.color = nodeColor;
    sprite.textHeight = 6;
    sprite.position.set(0, size + 8, 0);
    group.add(sprite);
    
    if (selectedNode && selectedNode.id === node.id) {
      const ringGeometry = new THREE.TorusGeometry(size * 1.5, 1, 16, 100);
      const ringMaterial = new THREE.MeshBasicMaterial({ color: nodeColor });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = Math.PI / 2;
      group.add(ring);
    }
    
    return group;
  };

  return (
    <ForceGraph3D
      ref={fgRef}
      graphData={filteredData}
      nodeThreeObject={nodeThreeObject}
      nodeLabel={node => `
        <div style="background: rgba(0,0,0,0.9); padding: 12px; border-radius: 6px; border: 2px solid ${getNodeColor(node)};">
          <div style="color: ${getNodeColor(node)}; font-weight: bold; margin-bottom: 4px;">${node.label}</div>
          <div style="color: #fff; font-size: 11px;">Date: ${node.date}</div>
          <div style="color: #fff; font-size: 11px;">Group: ${node.group}</div>
        </div>
      `}
      onNodeClick={onNodeClick}
      onNodeRightClick={onNodeDoubleClick}
      linkColor={link => getEdgeColor(link.type)}
      linkWidth={link => getEdgeStyle(link.type).width}
      linkOpacity={link => getEdgeStyle(link.type).opacity}
      linkCurvature={link => {
        if (link.type === 'recursion_link') return 0.4;
        if (link.type === 'semantic_link') return 0.2;
        return 0;
      }}
      linkDirectionalParticles={link => {
        if (link.type === 'recursion_link') return 4;
        if (link.type === 'temporal_link') return 2;
        return 0;
      }}
      linkDirectionalParticleSpeed={0.005}
      linkDirectionalParticleWidth={2}
      linkDirectionalParticleColor={link => getEdgeColor(link.type)}
      backgroundColor="#000000"
      cameraPosition={{ z: 1000 }}
      warmupTicks={100}
      cooldownTicks={0}
      d3AlphaDecay={0.02}
      d3VelocityDecay={0.3}
      d3Force={{
        charge: { strength: -2000 },
        link: { distance: 300 }
      }}
      enableNodeDrag={true}
      enableNavigationControls={true}
    />
  );
};

export default Graph3D;